import React, {createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback, useMemo} from 'react';

interface MapBoundingBox {
    width: number;
    height: number;
    top: number;
    left: number;
}

interface MapBoundingBoxContextValue {
    boundingBox: MapBoundingBox | null;
    setMapRef: (ref: HTMLElement | null) => void;
}

const MapBoundingBoxContext = createContext<MapBoundingBoxContextValue | undefined>(undefined);

interface MapBoundingBoxProviderProps {
    children: ReactNode;
}

/**
 * Provider that tracks the map container's bounding box
 * Updates on window resize with debouncing
 */
export function MapBoundingBoxProvider({children}: MapBoundingBoxProviderProps) {
    const [boundingBox, setBoundingBox] = useState<MapBoundingBox | null>(null);
    const mapRefState = useRef<HTMLElement | null>(null);
    const resizeTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const updateBoundingBox = useCallback(() => {
        if (mapRefState.current) {
            const rect = mapRefState.current.getBoundingClientRect();
            const newBox = {
                width: rect.width,
                height: rect.height,
                top: rect.top,
                left: rect.left,
            };
            
            // Only update if dimensions actually changed (prevent unnecessary re-renders)
            setBoundingBox((prev) => {
                if (!prev) return newBox;
                if (
                    Math.abs(prev.width - newBox.width) < 1 &&
                    Math.abs(prev.height - newBox.height) < 1 &&
                    Math.abs(prev.top - newBox.top) < 1 &&
                    Math.abs(prev.left - newBox.left) < 1
                ) {
                    return prev; // No change
                }
                return newBox;
            });
        }
    }, []);

    const setMapRef = useCallback((ref: HTMLElement | null) => {
        mapRefState.current = ref;
        if (ref) {
            updateBoundingBox();
        }
    }, [updateBoundingBox]);

    useEffect(() => {
        const handleResize = () => {
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
            resizeTimerRef.current = setTimeout(() => {
                updateBoundingBox();
            }, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
        };
    }, [updateBoundingBox]);

    const contextValue = useMemo(
        () => ({ boundingBox, setMapRef }),
        [boundingBox, setMapRef]
    );

    return (
        <MapBoundingBoxContext.Provider value={contextValue}>
            {children}
        </MapBoundingBoxContext.Provider>
    );
}

/**
 * Hook to access the map's bounding box
 * Returns null if map hasn't been measured yet
 */
export function useMapBoundingBox(): MapBoundingBox {
    const context = useContext(MapBoundingBoxContext);
    if (context === undefined) {
        throw new Error('useMapBoundingBox must be used within a MapBoundingBoxProvider');
    }
    return context.boundingBox || { width: 0, height: 0, top: 0, left: 0 } as MapBoundingBox;
}

/**
 * Hook to register the map container element
 * Call this from the map component with a ref
 */
export function useMapBoundingBoxRef() {
    const context = useContext(MapBoundingBoxContext);
    if (context === undefined) {
        throw new Error('useMapBoundingBoxRef must be used within a MapBoundingBoxProvider');
    }
    return context.setMapRef;
}
